/**
 * Drag ghost (INVENTORY §2 row 9; prototype lines 1260-1267).
 *
 * Mounted by App whenever `s.drag` is non-null. This file only paints — the
 * gesture lives in `lib/drag.ts`, which positions this node IMPERATIVELY:
 * `registerGhost` hands the element to the drag module, and every pointermove
 * writes `el.style.transform` directly. Pointer coordinates never enter React
 * state (that is why the prototype kept a `ghostRef`), so this component
 * re-renders only when the dragged game changes.
 */

import { useCallback } from 'react';

import { CoverArt } from '../components/CoverArt.tsx';
import { useArt } from '../components/useArt.ts';
import { registerGhost } from '../lib/drag.ts';
import { useQd } from '../store/index.ts';
import { dragGhostVM } from '../store/selectors.3d.ts';

export default function DragGhost(): JSX.Element | null {
  // Whole-store subscription, as in App/Crew/Login: a VM selector mints a fresh
  // object every call, which zustand 5's `Object.is` snapshot check rejects.
  const drag = useQd((s) => s.drag);
  const v = dragGhostVM(useQd());
  // The ghost is the same 2/3 portrait the board capsule was, so it hits the
  // same `id + 'p'` cache record and paints already-resolved art on frame one.
  const art = useArt((drag ? drag.id : '') + 'p', v.coverCands);
  const ref = useCallback((el: HTMLDivElement | null) => registerGhost(el), []);

  if (!v.show) return null;

  return (
    <div ref={ref} style={{ position: 'fixed', left: 0, top: 0, zIndex: 120, pointerEvents: 'none', willChange: 'transform' }}>
      <div
        style={{
          width: 68,
          aspectRatio: '2/3',
          border: '2px solid var(--acc)',
          background: v.fall,
          overflow: 'hidden',
          boxShadow: '6px 6px 0 rgba(0,0,0,.6)',
          position: 'relative',
        }}
      >
        <CoverArt style={{ position: 'absolute', inset: 0 }} fs={10} ini={v.ini} hasCover={art.hasCover} cover={art.cover} />
      </div>
      <div
        style={{
          marginTop: 6,
          background: 'var(--gt-card)',
          border: '1px solid var(--acc)',
          fontFamily: 'var(--gt-font-mono)',
          fontSize: '8.5px',
          color: 'var(--acc)',
          padding: '4px 6px',
          textAlign: 'center',
          maxWidth: 88,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          boxSizing: 'border-box',
        }}
      >
        {v.name}
      </div>
    </div>
  );
}
